/**
 * iOS Safari "Add to Home Screen" nudge.
 *
 * Quietly suggests a one-time install to anyone using the Hub in mobile
 * Safari. Hides itself when:
 *   - the user is already running it from the home screen (standalone),
 *   - the user is not on iOS Safari (Android Chrome shows its own native
 *     install banner; nothing to do for desktop),
 *   - the user dismissed the banner before (persisted to localStorage so
 *     the nudge doesn't keep harassing them).
 *
 * Why bother: Safari's Intelligent Tracking Prevention nukes a site's
 * storage after 7 days of no opens, which signs the user out. Installed
 * web apps don't get that purge, so the home-screen install effectively
 * makes the Hub "stay signed in forever" on iPhone. Worth one banner.
 */

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'ldg-hub:install-prompt-dismissed';

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  if (!isIos) return false;
  // CriOS = Chrome on iOS, FxiOS = Firefox on iOS, EdgiOS = Edge on iOS.
  // Those wrap WebKit so they have similar storage behavior, but their
  // "Add to Home Screen" gesture lives in a different place — we'd give
  // wrong instructions. Skip them; Safari only.
  if (/CriOS|FxiOS|EdgiOS/i.test(ua)) return false;
  return true;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS-specific flag set when launched from the home screen.
  const navAny = window.navigator as Navigator & { standalone?: boolean };
  if (navAny.standalone) return true;
  // Modern PWA check (Android, desktop).
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  return false;
}

const bannerStyle: React.CSSProperties = {
  position: 'fixed',
  left: 'var(--space-3)',
  right: 'var(--space-3)',
  bottom: 'calc(env(safe-area-inset-bottom, 0) + var(--space-3))',
  background: 'var(--white)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-3) var(--space-4)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
  boxShadow: 'var(--shadow-lg)',
  zIndex: 100,
  fontSize: 13,
  color: 'var(--ink)',
};

const dismissBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--ink-soft)',
  fontSize: 12,
  fontWeight: 600,
  border: 0,
  padding: '6px 10px',
  cursor: 'pointer',
  alignSelf: 'flex-end',
};

function InstallPrompt() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer the decision a tick so we don't flash the banner during the
    // initial render of the auth-loading state.
    const id = window.setTimeout(() => {
      if (!isIosSafari()) return;
      if (isStandalone()) return;
      try {
        if (window.localStorage.getItem(STORAGE_KEY) === '1') return;
      } catch {
        // localStorage getter can throw (Safari "Block All Cookies"); skip the nudge.
        return;
      }
      setVisible(true);
    }, 800);
    return () => window.clearTimeout(id);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* localStorage blocked — just hide for this session */
    }
    setVisible(false);
  };

  return (
    <div className="no-print" style={bannerStyle}>
      <div style={{ fontWeight: 700, color: 'var(--ink)' }}>
        Add the Hub to your home screen
      </div>
      <div style={{ color: 'var(--ink-soft)', lineHeight: 1.4 }}>
        Tap <strong>Share</strong> at the bottom of Safari, then
        <strong> Add to Home Screen</strong>. Keeps you signed in, opens like a
        real app, and it is the only way an iPhone will let the studio send you
        notifications.
      </div>
      <button type="button" style={dismissBtnStyle} onClick={dismiss}>
        Not now
      </button>
    </div>
  );
}

export default InstallPrompt;
